import { AxiosError } from 'axios';
import i18next from 'i18next';

const GENERIC_ERROR = 'We could not complete the request. Please try again.';

const STATUS_MESSAGES: Record<number, string> = {
    400: 'Some details need attention. Please review and try again.',
    401: 'Your session has expired. Sign in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested record could not be found.',
    409: 'This record changed. Refresh and try again.',
    422: 'Some details need attention. Please review and try again.',
    429: 'Too many requests. Wait a moment and try again.',
    500: 'The service is temporarily unavailable. Please try again shortly.',
    502: 'The service is temporarily unavailable. Please try again shortly.',
    503: 'The service is temporarily unavailable. Please try again shortly.',
    504: 'The service is temporarily unavailable. Please try again shortly.',
};

const CODE_MESSAGES: Record<string, string> = {
    ERR_1000_UNAUTHORIZED: STATUS_MESSAGES[401],
    ERR_1001_INVALID_CREDENTIALS: 'The email or password is incorrect.',
    ERR_1002_TOKEN_EXPIRED: STATUS_MESSAGES[401],
    ERR_1003_TOKEN_INVALID: STATUS_MESSAGES[401],
    ERR_1004_INSUFFICIENT_PERMISSIONS: STATUS_MESSAGES[403],
    ERR_1005_ACCOUNT_LOCKED: 'This account is locked. Contact a platform administrator.',
    ERR_1006_FORBIDDEN: STATUS_MESSAGES[403],
    ERR_1007_EMAIL_NOT_VERIFIED: 'Verify your email before continuing.',
    ERR_1100_VALIDATION_ERROR: STATUS_MESSAGES[422],
    ERR_1101_INVALID_INPUT: STATUS_MESSAGES[422],
    ERR_1102_MISSING_REQUIRED_FIELD: 'Complete the required fields and try again.',
    ERR_1103_INVALID_FORMAT: STATUS_MESSAGES[422],
    ERR_1104_VALUE_OUT_OF_RANGE: STATUS_MESSAGES[422],
    ERR_1200_RESOURCE_NOT_FOUND: STATUS_MESSAGES[404],
    ERR_1201_RESOURCE_ALREADY_EXISTS: 'A matching record already exists.',
    ERR_1202_RESOURCE_CONFLICT: STATUS_MESSAGES[409],
    ERR_1700_RATE_LIMIT_EXCEEDED: STATUS_MESSAGES[429],
    ERR_1804_CROSS_TENANT_ACCESS_DENIED: STATUS_MESSAGES[403],
    ERR_1900_INTERNAL_SERVER_ERROR: STATUS_MESSAGES[500],
    ERR_1901_SERVICE_UNAVAILABLE: STATUS_MESSAGES[503],
};

/**
 * API-03: codes emitted by the platform API (backend/src/modules/platform/lib/platformErrors.ts).
 * Values are [i18n key, English default]. Resolved at call time so the active
 * language applies; falls back to the default when i18next is not initialised.
 */
const PLATFORM_CODE_MESSAGES: Record<string, [string, string]> = {
    PLATFORM_UNAUTHORIZED: ['errors.platform.unauthorized', 'Your session has expired. Sign in again.'],
    PLATFORM_TOKEN_EXPIRED: ['errors.platform.unauthorized', 'Your session has expired. Sign in again.'],
    PLATFORM_TOKEN_INVALID: ['errors.platform.unauthorized', 'Your session has expired. Sign in again.'],
    PLATFORM_INVALID_CREDENTIALS: ['errors.platform.invalidCredentials', 'The email or password is incorrect.'],
    PLATFORM_FORBIDDEN: ['errors.platform.forbidden', 'Your role does not allow this action. Ask a platform admin if you need access.'],
    PLATFORM_MFA_REQUIRED: ['errors.platform.mfaStepUp', 'Confirm it is you with your authenticator code to continue.'],
    PLATFORM_MFA_STEP_UP_REQUIRED: ['errors.platform.mfaStepUp', 'Confirm it is you with your authenticator code to continue.'],
    PLATFORM_MFA_INVALID: ['errors.platform.mfaInvalid', 'That authenticator code was not accepted. Try again.'],
    PLATFORM_FEATURE_DISABLED: ['errors.platform.featureDisabled', 'This feature is switched off right now. Ask a platform admin to turn it on.'],
    PLATFORM_NOT_FOUND: ['errors.platform.notFound', 'The requested record could not be found.'],
    PLATFORM_VALIDATION_FAILED: ['errors.platform.validation', 'Some details need attention. Please review and try again.'],
    SETTING_VALIDATION_FAILED: ['errors.platform.settingValidation', 'This value is not allowed for this setting. Check it and try again.'],
    PLATFORM_CONFLICT: ['errors.platform.conflict', 'This record changed. Refresh and try again.'],
    PLATFORM_RATE_LIMITED: ['errors.platform.rateLimited', 'Too many requests. Wait a moment and try again.'],
    PLATFORM_IDEMPOTENCY_REPLAY: ['errors.platform.idempotencyReplay', 'This was already submitted. Refresh to see the result.'],
    PLATFORM_IDEMPOTENCY_MISMATCH: ['errors.platform.idempotencyMismatch', 'This was already submitted with different details. Refresh and start again.'],
    PLATFORM_INTERNAL_ERROR: ['errors.platform.internal', 'Something went wrong on our side. Please try again shortly.'],
    PLATFORM_SERVICE_UNAVAILABLE: ['errors.platform.serviceUnavailable', 'This service is temporarily unavailable. Please try again shortly.'],
};

function translate(key: string, defaultValue: string): string {
    if (!i18next.isInitialized) return defaultValue;
    const value = i18next.t(key, { defaultValue });
    return typeof value === 'string' && value.length > 0 ? value : defaultValue;
}

function codeMessage(code: string | undefined): string | undefined {
    if (!code) return undefined;
    const platform = PLATFORM_CODE_MESSAGES[code];
    if (platform) return translate(platform[0], platform[1]);
    return CODE_MESSAGES[code];
}

/** Machine code from an Axios error or a raw `{ error: { code } }` envelope. */
export function platformErrorCode(error: unknown): string | undefined {
    const responseData = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data;
    const envelope = (error instanceof AxiosError || responseData ? responseData : error) as ErrorEnvelope | undefined;
    const payload = envelope?.error;
    return typeof payload === 'object' && payload && typeof payload.code === 'string' ? payload.code : undefined;
}

/** True for the requireRecentMfa / login-MFA challenge: open the step-up dialog, do not toast. */
export function isMfaChallengeError(error: unknown): boolean {
    const code = platformErrorCode(error);
    return code === 'PLATFORM_MFA_STEP_UP_REQUIRED' || code === 'PLATFORM_MFA_REQUIRED';
}

/** True when the backend reports the feature is switched off by configuration. */
export function isFeatureDisabledError(error: unknown): boolean {
    return platformErrorCode(error) === 'PLATFORM_FEATURE_DISABLED';
}

/**
 * Whether an error should be shown as an error toast/banner. MFA challenges are
 * a prompt (MfaStepUpDialog), not a failure.
 */
export function shouldToastPlatformError(error: unknown): boolean {
    return !isMfaChallengeError(error);
}

/**
 * The ability the backend said was missing on a 403 (platformRequireAbility puts
 * `details.abilities_required: [{ action, subject }]` on PLATFORM_FORBIDDEN).
 * Returns e.g. "manage:PlatformUser", or undefined when absent.
 */
export function forbiddenAbility(error: unknown): string | undefined {
    const envelope = (error instanceof AxiosError ? error.response?.data : error) as
        | { error?: { details?: { abilities_required?: Array<{ action?: unknown; subject?: unknown }> } } }
        | undefined;
    const first = envelope?.error?.details?.abilities_required?.[0];
    if (!first || typeof first.action !== 'string' || typeof first.subject !== 'string') return undefined;
    if (!/^[a-z]+$/.test(first.action) || !/^[A-Za-z]+$/.test(first.subject)) return undefined;
    return `${first.action}:${first.subject}`;
}

type ErrorEnvelope = {
    error?: {
        code?: string;
        message?: string;
    } | string;
    message?: string;
};

export function platformUserError(error: unknown, fallback = GENERIC_ERROR): string {
    if (!error) return fallback;

    if (error instanceof AxiosError) {
        const envelope = error.response?.data as ErrorEnvelope | undefined;
        const payload = envelope?.error;
        const code = typeof payload === 'object' ? payload.code : undefined;
        const message = typeof payload === 'object' ? payload.message : typeof payload === 'string' ? payload : envelope?.message;

        const mapped = codeMessage(code);
        if (mapped) return mapped;
        if (isSafeBusinessMessage(message)) return message.trim();
        if (error.response?.status && STATUS_MESSAGES[error.response.status]) {
            return STATUS_MESSAGES[error.response.status];
        }
        if (error.code === 'ECONNABORTED') return 'The request timed out. Please try again.';
        if (!error.response) return 'We could not reach the service. Check the connection and try again.';
        return fallback;
    }

    const envelope = error as ErrorEnvelope;
    const payload = envelope?.error;
    const code = typeof payload === 'object' ? payload.code : undefined;
    const message = typeof payload === 'object' ? payload.message : typeof payload === 'string' ? payload : envelope?.message;

    const mapped = codeMessage(code);
    if (mapped) return mapped;
    if (isSafeBusinessMessage(message)) return message.trim();
    if (error instanceof Error && isSafeBusinessMessage(error.message)) return error.message.trim();
    return fallback;
}

export function isSafeBusinessMessage(message: unknown): message is string {
    if (typeof message !== 'string') return false;
    const trimmed = message.trim();
    if (trimmed.length === 0 || trimmed.length > 180) return false;

    const lowered = trimmed.toLowerCase();
    const technicalMarkers = [
        'axios',
        'stack',
        'trace',
        'syntaxerror',
        'typeerror',
        'referenceerror',
        'network error',
        'timeout of ',
        'http ',
        '/api/',
        'api key',
        'select ',
        'insert ',
        'update ',
        'delete ',
        'constraint',
        'postgres',
        'sql',
        'stripe',
        'webhook',
        'secret',
        'token',
        'price_',
        'prod_',
        'cus_',
        'sub_',
        'pi_',
    ];

    if (technicalMarkers.some((marker) => lowered.includes(marker))) return false;
    if (/^err_\d+/i.test(trimmed)) return false;
    if (/[{}[\]]/.test(trimmed)) return false;
    return true;
}
